import {
  CSSProperties,
  ComponentPropsWithoutRef,
  forwardRef,
  useEffect,
  useId,
  MouseEvent,
} from "react";
import { EditorContent } from "@tiptap/react";
import cx from "classnames";

import { FieldLabel } from "../../../components/FieldLabel";
import { Helper } from "../../../internal/components";
import { useLayoutPropsUtil } from "../../../internal/hooks";
import { useTrackingId } from "../../../hooks";
import { childrenToString } from "../../../internal/functions";
import { RichTextEditorToolbar } from "./internal/RichTextEditorToolbar";
import { RichTextEditorDragHandle } from "./internal/RichTextEditorDragHandle";
import { MentionPopover } from "./internal/MentionPopover";
import { LinkPopover } from "./internal/LinkPopover";
import { YouTubePopover } from "./internal/YouTubePopover";
import { YouTubeEmbedPrompt } from "./internal/YouTubeEmbedPrompt";
import { RichTextEditorProvider } from "./internal/RichTextEditorProvider";
import { useRichTextEditorContext } from "./internal/RichTextEditorContext";
import { RichTextEditorProps } from "./types";
import styles from "./RichTextEditor.module.scss";

type RichTextEditorContentProps = ComponentPropsWithoutRef<"div"> &
  Pick<
    RichTextEditorProps,
    | "id"
    | "disabled"
    | "error"
    | "hint"
    | "description"
    | "label"
    | "labelProps"
    | "required"
    | "moreInfo"
    | "className"
    | "style"
    | "renderMentionOption"
    | "data-tracking-id"
  > & {
    layoutStyles?: CSSProperties;
  };

const RichTextEditorContent = forwardRef<
  HTMLDivElement,
  RichTextEditorContentProps
>(
  (
    {
      id: idProp,
      disabled,
      error,
      hint,
      description,
      label,
      labelProps,
      required,
      moreInfo,
      className,
      style,
      layoutStyles,
      renderMentionOption,
      "data-tracking-id": dataTrackingIdOverride,
      ...rest
    },
    ref,
  ) => {
    const {
      editor,
      allowLink,
      allowMedia,
      allowMention,
      allowDragDrop,
      mentionState,
      mentionPopoverRef,
      linkPopoverState,
      youtubePopoverState,
      youtubeEmbedPromptState,
      embedYoutubeFromPrompt,
      closeYoutubeEmbedPrompt,
      onEditorClick,
      closeLinkPopover,
      removeLinkPopover,
      saveLinkPopover,
      closeYoutubePopover,
      saveYoutubePopover,
    } = useRichTextEditorContext();

    const data = {
      label: childrenToString(label),
      labelProps,
      hint: childrenToString(hint),
      description: childrenToString(description),
    };
    const trackingId = useTrackingId({
      name: "RichTextEditor",
      data,
      hasOverride: !!dataTrackingIdOverride,
    });

    const editorUid = useId();
    const editorId = idProp ?? editorUid;
    const labelUid = useId();
    const helperUid = useId();
    const moreInfoUid = useId();

    const errorMessages =
      typeof error === "boolean" || error === undefined ? undefined : error;
    const hasHelperText = hint || errorMessages || description;

    const ariaDescribedBy =
      [hasHelperText && helperUid, moreInfo && moreInfoUid]
        .filter(Boolean)
        .join(" ") || undefined;

    useEffect(() => {
      if (!editor) return;
      const attributes: Record<string, string> = {
        id: editorId,
        role: "textbox",
        "aria-multiline": "true",
      };
      if (label) attributes["aria-labelledby"] = labelProps?.id ?? labelUid;
      if (ariaDescribedBy) attributes["aria-describedby"] = ariaDescribedBy;
      if (error) attributes["aria-invalid"] = "true";
      if (required) attributes["aria-required"] = "true";
      if (trackingId) attributes["data-tracking-id"] = trackingId;
      editor.setOptions({ editorProps: { attributes } });
    }, [
      editorId,
      label,
      labelProps?.id,
      labelUid,
      ariaDescribedBy,
      error,
      required,
      trackingId,
      editor,
    ]);

    const handleLabelClick = (e: MouseEvent) => {
      labelProps?.onClick?.(e);
      if (editor && !disabled) {
        editor.commands.focus();
      }
    };

    return (
      <div
        ref={ref}
        data-anv="rich-text-editor"
        className={cx(styles["rte"], className)}
        style={{ ...style, ...layoutStyles }}
        {...rest}
      >
        {label && (
          <FieldLabel
            el="div"
            id={labelUid}
            required={required}
            moreInfo={moreInfo}
            moreInfoId={moreInfo ? moreInfoUid : undefined}
            {...labelProps}
            onClick={handleLabelClick}
          >
            {label}
          </FieldLabel>
        )}

        <div
          className={cx(styles["editor-wrapper"], {
            [styles["error"]]: !!error,
            [styles["disabled"]]: disabled,
          })}
        >
          <RichTextEditorToolbar />
          {editor && allowDragDrop && (
            <RichTextEditorDragHandle editor={editor} />
          )}
          <EditorContent
            editor={editor}
            className={cx(
              styles["editor"],
              allowDragDrop && styles["editor--drag"],
            )}
            onClick={onEditorClick}
          />
        </div>

        {hasHelperText && (
          <Helper
            id={helperUid}
            hint={hint}
            description={description}
            error={
              typeof errorMessages === "string" || Array.isArray(errorMessages)
                ? errorMessages
                : undefined
            }
          />
        )}

        {allowMention && (
          <MentionPopover
            ref={mentionPopoverRef}
            open={mentionState.open}
            rect={mentionState.rect}
            items={mentionState.items}
            command={mentionState.command}
            renderOption={renderMentionOption}
          />
        )}

        {allowLink && (
          <LinkPopover
            open={linkPopoverState.open}
            anchorRect={linkPopoverState.anchorRect}
            initialHref={linkPopoverState.initialHref}
            initialDisplayText={linkPopoverState.initialDisplayText}
            onSave={saveLinkPopover}
            onRemove={removeLinkPopover}
            onCancel={closeLinkPopover}
          />
        )}

        {allowMedia && (
          <YouTubePopover
            open={youtubePopoverState.open}
            anchorRect={youtubePopoverState.anchorRect}
            onSave={saveYoutubePopover}
            onCancel={closeYoutubePopover}
          />
        )}

        {allowMedia && (
          <YouTubeEmbedPrompt
            open={youtubeEmbedPromptState.open}
            anchorRect={youtubeEmbedPromptState.anchorRect}
            onEmbed={embedYoutubeFromPrompt}
            onDismiss={closeYoutubeEmbedPrompt}
          />
        )}
      </div>
    );
  },
);

RichTextEditorContent.displayName = "RichTextEditorContent";

/**
 * RichTextEditor component for authoring formatted content.
 *
 * Features:
 * - Supports both controlled (`value`) and uncontrolled (`defaultValue`) modes
 * - Emits content as HTML (`onChange`), TipTap JSON (`onJsonChange`), and markdown (`onMarkdownChange`)
 * - Accepts markdown as input via `contentType="markdown"`
 * - Extensible via `extensions` for custom nodes/marks not covered by the built-in toolbar
 * - Text formatting toolbar: bold, italic, underline, strikethrough, inline code, blockquote
 * - Headings (H1–H3), paragraph, font size, text alignment, and indentation
 * - Bullet lists, ordered lists, and checklists
 * - Link insertion and removal via popover
 * - Image upload and YouTube video embedding via Insert dropdown (`allowMedia`), with `src` restricted to `http`/`https` by default (`isAllowedImageSrc`)
 * - Drag handle for reordering content blocks (`allowDragDrop`)
 * - @mention typeahead with hover card support (`allowMention`)
 * - Disabled state disables both toolbar and editor content
 * - Field-level label, hint, description, and error messaging
 * - Accessible toolbar with ARIA roles, `aria-pressed` toggles, and keyboard navigation
 * - Built on TipTap (ProseMirror) with Shadow DOM support
 *
 * @example
 * <RichTextEditor
 *   label="Notes"
 *   defaultValue="<p>Hello world</p>"
 *   onChange={(html) => console.log(html)}
 * />
 */
export const RichTextEditor = forwardRef<HTMLDivElement, RichTextEditorProps>(
  (props, ref) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      id,
      value,
      defaultValue,
      onChange,
      onJsonChange,
      contentType,
      onMarkdownChange,
      extensions,
      placeholder,
      disabled = false,
      error,
      hint,
      description,
      label,
      labelProps,
      required,
      moreInfo,
      className,
      style,
      allowTextFormat = true,
      allowTextAlignment = true,
      allowLists = false,
      allowChecklist = false,
      allowCode = false,
      allowBlockquote = false,
      allowLink = false,
      allowMedia = false,
      allowMention = false,
      allowDragDrop = false,
      mentionOptions,
      onMentionInputChange,
      renderMentionOption,
      renderMentionHoverContent,
      onImageUpload,
      allowedMimeTypes,
      isAllowedImageSrc,
      ...rest
    } = componentProps;

    return (
      <RichTextEditorProvider
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        onJsonChange={onJsonChange}
        contentType={contentType}
        onMarkdownChange={onMarkdownChange}
        extensions={extensions}
        placeholder={placeholder}
        disabled={disabled}
        allowTextFormat={allowTextFormat}
        allowTextAlignment={allowTextAlignment}
        allowLists={allowLists}
        allowChecklist={allowChecklist}
        allowCode={allowCode}
        allowBlockquote={allowBlockquote}
        allowLink={allowLink}
        allowMedia={allowMedia}
        allowMention={allowMention}
        allowDragDrop={allowDragDrop}
        mentionOptions={mentionOptions}
        onMentionInputChange={onMentionInputChange}
        renderMentionHoverContent={renderMentionHoverContent}
        onImageUpload={onImageUpload}
        allowedMimeTypes={allowedMimeTypes}
        isAllowedImageSrc={isAllowedImageSrc}
      >
        <RichTextEditorContent
          {...rest}
          ref={ref}
          id={id}
          layoutStyles={layoutStyles}
          disabled={disabled}
          data-tracking-id={props["data-tracking-id"]}
          error={error}
          hint={hint}
          description={description}
          label={label}
          labelProps={labelProps}
          required={required}
          moreInfo={moreInfo}
          className={className}
          style={style}
          renderMentionOption={renderMentionOption}
        />
      </RichTextEditorProvider>
    );
  },
);

RichTextEditor.displayName = "RichTextEditor";
